/* @flow */
import {
    useQuery,
    type QueryResult,
    useMutation,
    type MutationResult
} from 'react-query';
import * as Immutable from 'immutable';

import useClientId from 'common/hooks/useClientId';

import { team as teamApi } from 'common/middleware/api';
import { teamSettings as teamSettingsApi } from 'platform/api';
import { Team } from 'common/models/internal/Team';
import type { TeamSettingsRequestModelType } from 'platform/settings/models/flow/TeamSettings';
import { TeamSettingsResponse } from 'platform/settings/models/internal/TeamSettings';

type Response = Immutable.List<Team>;

export const useTeams = (): QueryResult<Response> => {
    const clientId = useClientId();
    return useQuery({
        queryKey: ['client', clientId, 'teams'],
        queryFn: async () => {
            const data = await teamApi.fetch({ clientId });
            return Immutable.List(data).map(team => Team.fromJS(team));
        }
    });
};

type Params = {
    teamId: string
};

export const useTeamSettingsByTeamId = (): MutationResult<
    Params,
    TeamSettingsResponse
> => {
    const clientId = useClientId();
    return useMutation(async ({ teamId }: Params) => {
        const data = await teamSettingsApi.fetch({ clientId, teamId });
        return TeamSettingsResponse.fromJS(data);
    });
};

type UpdateTeamSettingsParams = {
    teamId: string,
    requestPayload: TeamSettingsRequestModelType
};

export const useUpdateTeamSettingsByTeamId = (): MutationResult<
    UpdateTeamSettingsParams,
    TeamSettingsResponse
> => {
    const clientId = useClientId();
    return useMutation(
        async ({ teamId, requestPayload }: UpdateTeamSettingsParams) => {
            const data = await teamSettingsApi.post(
                { clientId, teamId },
                requestPayload
            );
            return TeamSettingsResponse.fromJS(data);
        }
    );
};
